import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Student, Teacher } from '../../../utils/firebase';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../../utils/firebase';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import HorseRacingLottery from './lottery/HorseRacingLottery';
import PinballLottery from './lottery/PinballLottery';
import ChopstickLottery from './lottery/ChopstickLottery';
import MarbleLottery from './lottery/MarbleLottery';

const Lottery: React.FC = () => {
  const { currentUser } = useAuth();
  const teacher = currentUser as Teacher;
  
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'horse' | 'pinball' | 'chopstick' | 'marble'>('horse');
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<0 | 1 | 2 | 3>(0); // 0: 뽑기 유형 선택, 1: 참가자 선택, 2: 추첨 방식 선택, 3: 추첨 실행
  const [lotteryType, setLotteryType] = useState<'person' | 'item'>('person'); // 'person': 사람 뽑기, 'item': 항목 뽑기

  // 학생 목록 가져오기
  useEffect(() => {
    if (!teacher?.uid || !teacher.homeroomClass) return;

    try {
      const studentsRef = collection(db, `schools/${teacher.schoolId}/classes/${teacher.classId}/students`);
      const q = query(studentsRef);
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const studentsData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            ...data,
            uid: doc.id,
            loginType: data.loginType === 'social' ? doc.id : undefined
          } as Student;
        });
        
        setStudents(studentsData || []);
        // 초기에는 선택하지 않음
        setSelectedStudents([]);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (err) {
      console.error('학생 목록 로딩 중 오류:', err);
      setLoading(false);
    }
  }, [teacher?.uid, teacher?.schoolId, teacher?.classId, teacher?.homeroomClass]);

  // 전체 선택/해제
  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.uid));
    }
  };

  // 개별 선택/해제
  const handleToggleStudent = (studentId: string) => {
    setSelectedStudents(prev => 
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const selectedStudentsList = students.filter(s => selectedStudents.includes(s.uid));

  // 다음 단계로 이동
  const handleNext = () => {
    if (step === 0) {
      // 뽑기 유형 선택 후
      if (lotteryType === 'person') {
        // 사람 뽑기 선택 시 참가자 선택 단계로
        setStep(1);
      } else {
        // 항목 뽑기 선택 시 항목 입력 단계로 (추후 구현)
        // 일단 추첨 방식 선택으로 이동
        setStep(2);
      }
    } else if (step === 1) {
      // 참가자 선택 후 추첨 방식 선택으로
      if (selectedStudents.length > 0) {
        setStep(2);
      }
    }
  };

  // 추첨 방식 선택
  const handleSelectLotteryMethod = (method: 'horse' | 'pinball' | 'chopstick' | 'marble') => {
    setActiveTab(method);
    setStep(3);
  };

  // 이전 단계로 이동
  const handleBack = () => {
    if (step === 3) {
      setStep(2);
    } else if (step === 2) {
      if (lotteryType === 'person') {
        setStep(1);
      } else {
        setStep(0);
      }
    } else if (step === 1) {
      setStep(0);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div>로딩 중...</div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '1280px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.5rem', color: '#161616' }}>
        뽑기
      </h1>

      {step === 0 ? (
        /* 0단계: 뽑기 유형 선택 */
        <Card style={{ padding: '2rem' }}>
          <div>
            <h2 style={{ 
              fontSize: '1.5rem', 
              fontWeight: 600, 
              color: '#161616',
              textAlign: 'left',
              margin: 0
            }}>
              뽑기 유형 선택
            </h2>
            <p style={{ 
              fontSize: '1rem', 
              color: '#666',
              textAlign: 'left'
            }}>
              무엇을 뽑을지 선택해주세요.
            </p>
          </div>
          
          {/* 선택 영역 - SignupChoice 스타일 */}
          <div style={{
            alignSelf: 'stretch',
            paddingLeft: '28px',
            paddingRight: '28px',
            paddingTop: '24px',
            paddingBottom: '24px',
            background: 'var(--Color-background-sub, #F8F8F8)',
            borderRadius: '20px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: '8px',
            marginBottom: '2rem'
          }}>
            <div style={{
              alignSelf: 'stretch',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '24px'
            }}>
              {/* 사람 뽑기 버튼 */}
              <div
                onClick={() => setLotteryType('person')}
                style={{
                  width: 'auto',
                  cursor: 'pointer',
                  flex: '1 1 0'
                }}
              >
                <div style={{
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  paddingTop: '32px',
                  paddingBottom: '32px',
                  background: lotteryType === 'person' 
                    ? 'var(--Color-fill-normal, #293044)' 
                    : 'var(--Color-fill-white, white)',
                  borderRadius: '12px',
                  outline: lotteryType === 'person' 
                    ? 'none' 
                    : '1.20px solid var(--Color-line-normal, #F0F0F0)',
                  outlineOffset: lotteryType === 'person' ? '0' : '-1.20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    color: lotteryType === 'person' 
                      ? 'var(--Color-text-inverse, white)' 
                      : 'var(--Color-text-normal, #161616)',
                    fontSize: '16px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '600',
                    lineHeight: '24px',
                    wordWrap: 'break-word'
                  }}>
                    사람 뽑기
                  </div>
                </div>
              </div>

              {/* 항목 뽑기 버튼 */}
              <div
                onClick={() => setLotteryType('item')}
                style={{
                  width: 'auto',
                  cursor: 'pointer',
                  flex: '1 1 0'
                }}
              >
                <div style={{
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  paddingTop: '32px',
                  paddingBottom: '32px',
                  background: lotteryType === 'item' 
                    ? 'var(--Color-fill-normal, #293044)' 
                    : 'var(--Color-fill-white, white)',
                  borderRadius: '12px',
                  outline: lotteryType === 'item' 
                    ? 'none' 
                    : '1.20px solid var(--Color-line-normal, #F0F0F0)',
                  outlineOffset: lotteryType === 'item' ? '0' : '-1.20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <div style={{
                    color: lotteryType === 'item' 
                      ? 'var(--Color-text-inverse, white)' 
                      : 'var(--Color-text-normal, #161616)',
                    fontSize: '16px',
                    fontFamily: 'Pretendard, sans-serif',
                    fontWeight: '600',
                    lineHeight: '24px',
                    wordWrap: 'break-word'
                  }}>
                    항목 뽑기
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 다음으로 버튼 */}
          <Button
            type="button"
            width="100%"
            height="auto"
            padding="16px 20px"
            variant="primary"
            size="large"
            onClick={handleNext}
          >
            다음으로
          </Button>
        </Card>
      ) : step === 1 ? (
        /* 1단계: 참가자 선택 */
        <Card style={{ padding: '2rem' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#161616', textAlign: 'left', margin: 0 }}>
              참가자 선택
            </h2>
            <p style={{ fontSize: '1rem', color: '#666', textAlign: 'left', marginBottom: '1rem' }}>
              추첨에 참가할 학생을 선택해주세요.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <Button
                type="button"
                width="auto"
                height="auto"
                padding="12px 20px"
                variant={selectedStudents.length === students.length ? 'primary' : 'secondary'}
                size="medium"
                onClick={handleSelectAll}
              >
                {selectedStudents.length === students.length ? '전체 해제' : '전체 선택'}
              </Button>
              <span style={{ fontSize: '1rem', color: '#666', fontWeight: 500 }}>
                선택된 인원: {selectedStudents.length}명 / 전체: {students.length}명
              </span>
            </div>
          </div>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(6, 1fr)', 
            gap: '1rem',
            maxHeight: '500px',
            overflowY: 'auto',
            padding: '1rem',
            marginBottom: '2rem',
            width: '100%',
            boxSizing: 'border-box'
          }}>
            {[...students].sort((a, b) => a.name.localeCompare(b.name, 'ko')).map(student => (
              <label
                key={student.uid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  backgroundColor: selectedStudents.includes(student.uid) ? '#E8F4FD' : 'transparent',
                  border: selectedStudents.includes(student.uid) ? '2px solid #4A90E2' : '1px solid #E0E0E0',
                  transition: 'all 0.2s',
                  width: '100%',
                  boxSizing: 'border-box',
                  minWidth: 0
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedStudents.includes(student.uid)}
                  onChange={() => handleToggleStudent(student.uid)}
                  style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                />
                <span style={{ fontSize: '1.125rem', color: '#161616', fontWeight: 500 }}>
                  {student.name}
                </span>
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
            <Button
              type="button"
              width="auto"
              height="auto"
              padding="20px 24px"
              variant="cancel"
              size="large"
              onClick={handleBack}
            >
              ← 뒤로
            </Button>
            <Button
              type="button"
              width="100%"
              height="auto"
              padding="20px 24px"
              variant="primary"
              size="large"
              onClick={handleNext}
              disabled={selectedStudents.length === 0}
            >
              다음으로
            </Button>
          </div>
        </Card>
      ) : step === 2 ? (
        /* 2단계: 추첨 방식 선택 */
        <Card style={{ padding: '2rem' }}>
          <div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#161616', textAlign: 'left', margin: 0 }}>
              추첨 방식 선택
            </h2>
            <p style={{ fontSize: '1rem', color: '#666', textAlign: 'left', marginTop: '0.5rem', marginBottom: '2rem' }}>
              추첨 방식을 선택해주세요.
            </p>
          </div>

          {/* 선택된 참가자 표시 */}
          <Card style={{ marginBottom: '2rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
              선택된 참가자: {selectedStudentsList.length}명
            </div>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: '#161616'
            }}>
              {selectedStudentsList.map(student => (
                <span
                  key={student.uid}
                  style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#E8F4FD',
                    borderRadius: '0.25rem',
                    color: '#4A90E2'
                  }}
                >
                  {student.name}
                </span>
              ))}
            </div>
          </Card>

          {/* 추첨 방식 선택 버튼들 */}
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {[
              { id: 'horse' as const, label: '경마 방식', description: '학생들이 경마처럼 달려서 순위를 결정합니다' },
              { id: 'pinball' as const, label: 'MARU 핀볼 레이스', description: '팡팡 튀는 범퍼와 회전 패들! 마지막까지 짜릿한 역전 레이스' },
              { id: 'chopstick' as const, label: '젓가락 뽑기', description: '젓가락으로 순서대로 학생을 뽑습니다' },
              { id: 'marble' as const, label: '구슬 뽑기', description: '구슬통을 돌려 떨어진 구슬을 확인합니다' }
            ].map(method => (
              <button
                key={method.id}
                onClick={() => handleSelectLotteryMethod(method.id)}
                style={{
                  padding: '2rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  border: '2px solid #E0E0E0',
                  borderRadius: '0.5rem',
                  backgroundColor: '#FFFFFF',
                  color: '#161616',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  minHeight: '160px',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#4A90E2';
                  e.currentTarget.style.backgroundColor = '#F8FBFF';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#E0E0E0';
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                }}
              >
                <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>
                  {method.label}
                </div>
                <div style={{ fontSize: '1rem', color: '#666', fontWeight: 400 }}>
                  {method.description}
                </div>
              </button>
            ))}
          </div>

          {/* 뒤로가기 버튼 */}
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              type="button"
              onClick={handleBack}
              variant="cancel"
              padding="20px 24px"
            >
              ← 뒤로
            </Button>
          </div>
        </Card>
      ) : (
        /* 3단계: 추첨 실행 */
        <div>
          {/* 뒤로가기 버튼 */}
          <div style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              onClick={handleBack}
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                backgroundColor: 'transparent',
                color: '#666',
                border: '1px solid #E0E0E0',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              ← 뒤로
            </button>
          </div>
          
          {/* 선택된 참가자 표시 */}
          <Card style={{ marginBottom: '1.5rem', padding: '1rem' }}>
            <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>
              선택된 참가자: {selectedStudentsList.length}명
            </div>
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '0.5rem',
              fontSize: '0.875rem',
              color: '#161616'
            }}>
              {selectedStudentsList.map(student => (
                <span
                  key={student.uid}
                  style={{
                    padding: '0.25rem 0.75rem',
                    backgroundColor: '#E8F4FD',
                    borderRadius: '0.25rem',
                    color: '#4A90E2'
                  }}
                >
                  {student.name}
                </span>
              ))}
            </div>
          </Card>

          {/* 추첨 컴포넌트 */}
          <div>
            {activeTab === 'horse' && (
              <HorseRacingLottery students={selectedStudentsList} />
            )}
            {activeTab === 'pinball' && (
              <PinballLottery students={selectedStudentsList} />
            )}
            {activeTab === 'chopstick' && (
              <ChopstickLottery students={selectedStudentsList} />
            )}
            {activeTab === 'marble' && (
              <MarbleLottery students={selectedStudentsList} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Lottery;

